/**
 * logger — 前端日志工具
 * 开发环境输出到 console，生产环境通过 IPC 转发给 Rust
 *
 * 日志策略（生产环境）：
 * - error → 立即通过 IPC 上报到 Rust → AIHelperBoss
 * - warn + info → 批量缓冲（50条/30s），写入 Rust 本地日志文件
 * - debug → 仅开发环境输出，生产环境静默丢弃
 *
 * 使用方式：
 *   import { logger } from '@/utils/logger'
 *   logger.info('Auth', '登录成功', { userId: 123 })
 */
import { ipcCall } from '@/composables/useIPC'
import { isDesktopRuntime } from '@/platform/bridge'
import { env } from '@/utils/env'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogEntry {
  level: LogLevel
  module: string
  message: string
  timestamp: string
  data?: unknown
}

/** 批量上报缓冲区 */
const buffer: LogEntry[] = []
const FLUSH_INTERVAL = 30_000
const FLUSH_SIZE = 50
let flushTimer: ReturnType<typeof setInterval> | null = null
const LOG_STORAGE_KEY = '__aihelper_log_buffer__'

/** 格式化日志条目 */
function createEntry(level: LogLevel, module: string, message: string, data?: unknown): LogEntry {
  return {
    level,
    module,
    message,
    timestamp: new Date().toISOString(),
    data,
  }
}

/** 开发环境 console 输出（绕过 ESLint no-console） */
const consoleMethods: Record<LogLevel, (...args: unknown[]) => void> = {
  // eslint-disable-next-line no-console
  debug: console.debug,
  // eslint-disable-next-line no-console
  info: console.info,
  // eslint-disable-next-line no-console
  warn: console.warn,
  // eslint-disable-next-line no-console
  error: console.error,
}

function devLog(entry: LogEntry) {
  const tag = `[${entry.level.toUpperCase()}][${entry.module}]`
  if (entry.data !== undefined) {
    consoleMethods[entry.level](tag, entry.message, entry.data)
  } else {
    consoleMethods[entry.level](tag, entry.message)
  }
}

/** 通过 IPC 批量发送缓冲区日志到 Rust 端 */
async function flushBuffer() {
  if (!isDesktopRuntime) return
  if (buffer.length === 0) return
  const batch = buffer.splice(0, buffer.length)
  try {
    await ipcCall('log_batch_upload', { entries: batch })
  } catch {
    // Rust 端不可用时静默丢弃，不阻塞前端
    // eslint-disable-next-line no-console
    console.warn('[logger] flush failed, discarded', batch.length, 'entries')
  }
}

/** 写入日志 */
function log(level: LogLevel, module: string, message: string, data?: unknown) {
  const entry = createEntry(level, module, message, data)

  if (env.DEV) {
    devLog(entry)
    return
  }

  // 生产 Web 没有 Rust IPC。浏览器端日志由 DevTools/HTTP 链路负责，不能
  // 累积桌面日志或调用 log_batch_upload 制造无意义的控制台错误。
  if (!isDesktopRuntime) return

  // 生产环境 debug 级别静默丢弃
  if (level === 'debug') return

  // 生产环境 error → 立即上报（不等待缓冲，崩溃前确保信息不丢失）
  if (level === 'error') {
    ipcCall('log_batch_upload', { entries: [entry] }).catch(() => {
      // eslint-disable-next-line no-console
      console.warn('[logger] immediate error upload failed')
    })
    return
  }

  // 生产环境 warn + info → 推入缓冲区，达到阈值时批量 flush
  buffer.push(entry)
  if (buffer.length >= FLUSH_SIZE) {
    flushBuffer()
  }
}

/** 紧急保存：将未发送日志同步写入 localStorage（beforeunload 时 IPC 异步调用无法完成） */
function emergencySave() {
  if (!isDesktopRuntime) return
  if (buffer.length === 0) return
  try {
    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(buffer.splice(0)))
  } catch {
    // localStorage 写入失败静默忽略
  }
}

/** 恢复上次未发送的日志（启动时调用） */
function recoverBuffer() {
  if (!isDesktopRuntime) return
  try {
    const saved = localStorage.getItem(LOG_STORAGE_KEY)
    if (saved) {
      localStorage.removeItem(LOG_STORAGE_KEY)
      const entries = JSON.parse(saved) as LogEntry[]
      if (entries.length > 0) {
        ipcCall('log_batch_upload', { entries }).catch(() => {
          // eslint-disable-next-line no-console
          console.warn('[logger] recover flush failed, discarded', entries.length, 'entries')
        })
      }
    }
  } catch {
    // 解析失败静默忽略
  }
}

/** 启动定时刷新（main.ts 中调用） */
export function startLogFlush() {
  if (!isDesktopRuntime) return
  if (flushTimer) return
  // 恢复上次未发送的日志
  recoverBuffer()
  flushTimer = setInterval(flushBuffer, FLUSH_INTERVAL)
  window.addEventListener('beforeunload', () => {
    flushBuffer()    // 尝试异步发送（可能不完成）
    emergencySave()  // 同步保存到 localStorage 作为兆底
  })
}

/** 停止定时刷新 */
export function stopLogFlush() {
  if (!isDesktopRuntime) return
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
  // 最后一次刷新
  flushBuffer()
}

export const logger = {
  debug: (module: string, message: string, data?: unknown) => log('debug', module, message, data),
  info: (module: string, message: string, data?: unknown) => log('info', module, message, data),
  warn: (module: string, message: string, data?: unknown) => log('warn', module, message, data),
  error: (module: string, message: string, data?: unknown) => log('error', module, message, data),
}
